import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { ApiError, ApiErrorType, ApiResponse, RequestOptions } from './errors';

/**
 * API Configuration
 */
interface ApiConfig {
  baseURL: string;
  timeout: number;
  retries: number;
  retryDelay: number;
  enableLogging: boolean;
}

/**
 * Default Configuration
 */
const DEFAULT_CONFIG: ApiConfig = {
  baseURL: import.meta.env.VITE_API_ENDPOINT || '/',
  timeout: 30000, // 30 seconds
  retries: 3,
  retryDelay: 1000, // 1 second
  enableLogging: import.meta.env.DEV || false,
};

/**
 * Logger utility
 */
class Logger {
  private enabled: boolean;

  constructor(enabled: boolean) {
    this.enabled = enabled;
  }

  log(message: string, ...args: any[]) {
    if (this.enabled) {
      console.log(`[API] ${message}`, ...args);
    }
  }

  error(message: string, ...args: any[]) {
    if (this.enabled) {
      console.error(`[API Error] ${message}`, ...args);
    }
  }

  warn(message: string, ...args: any[]) {
    if (this.enabled) {
      console.warn(`[API Warning] ${message}`, ...args);
    }
  }
}

/**
 * Enhanced HTTP Client with error handling, retries, and logging
 */
export class HttpClient {
  private instance: AxiosInstance;
  private logger: Logger;
  private config: ApiConfig;

  constructor(config: Partial<ApiConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.logger = new Logger(this.config.enableLogging);

    this.instance = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors() {
    // Request interceptor
    this.instance.interceptors.request.use(
      (config) => {
        this.logger.log(`Request: ${config.method?.toUpperCase()} ${config.url}`, {
          params: config.params,
          data: config.data,
        });
        return config;
      },
      (error) => {
        this.logger.error('Request error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.instance.interceptors.response.use(
      (response) => {
        this.logger.log(`Response: ${response.status} ${response.config.url}`, {
          data: response.data,
        });
        return response;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  /**
   * Convert Axios error to ApiError
   */
  private handleError(error: any): ApiError {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;

      // Network error
      if (!axiosError.response) {
        if (axiosError.code === 'ECONNABORTED') {
          return new ApiError(
            'Request timeout',
            ApiErrorType.TIMEOUT_ERROR,
            undefined,
            axiosError.code
          );
        }
        return new ApiError(
          'Network error - Unable to connect to server',
          ApiErrorType.NETWORK_ERROR,
          undefined,
          axiosError.code,
          { originalError: axiosError.message }
        );
      }

      // HTTP error responses
      const status = axiosError.response.status;
      const data = axiosError.response.data;

      switch (status) {
        case 400:
          return new ApiError(
            'Bad request',
            ApiErrorType.VALIDATION_ERROR,
            status,
            'BAD_REQUEST',
            data
          );
        case 401:
          return new ApiError(
            'Authentication required',
            ApiErrorType.AUTHENTICATION_ERROR,
            status,
            'UNAUTHORIZED',
            data
          );
        case 403:
          return new ApiError(
            'Access forbidden',
            ApiErrorType.AUTHORIZATION_ERROR,
            status,
            'FORBIDDEN',
            data
          );
        case 404:
          return new ApiError(
            'Resource not found',
            ApiErrorType.NOT_FOUND_ERROR,
            status,
            'NOT_FOUND',
            data
          );
        case 422:
          return new ApiError(
            'Validation failed',
            ApiErrorType.VALIDATION_ERROR,
            status,
            'VALIDATION_ERROR',
            data
          );
        case 500:
        case 502:
        case 503:
        case 504:
          return new ApiError(
            'Server error',
            ApiErrorType.SERVER_ERROR,
            status,
            'SERVER_ERROR',
            data
          );
        default:
          return new ApiError(
            `HTTP Error ${status}`,
            status >= 500 ? ApiErrorType.SERVER_ERROR : ApiErrorType.CLIENT_ERROR,
            status,
            'HTTP_ERROR',
            data
          );
      }
    }

    // Unknown error
    return new ApiError(
      error.message || 'An unknown error occurred',
      ApiErrorType.UNKNOWN_ERROR,
      undefined,
      'UNKNOWN',
      error
    );
  }

  /**
   * Sleep utility for retry delay
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Retry logic for failed requests
   */
  private async retryRequest<T>(
    requestFn: () => Promise<AxiosResponse<T>>,
    retries: number,
    delay: number,
    error: ApiError
  ): Promise<ApiResponse<T>> {
    // Don't retry for client errors (4xx) except 408 (timeout) and 429 (rate limit)
    if (
      error.status &&
      error.status >= 400 &&
      error.status < 500 &&
      error.status !== 408 &&
      error.status !== 429
    ) {
      return {
        success: false,
        error,
        status: error.status,
        timestamp: new Date(),
      };
    }

    if (retries <= 0) {
      this.logger.error('Max retries reached', error.toJSON());
      return {
        success: false,
        error,
        status: error.status,
        timestamp: new Date(),
      };
    }

    this.logger.warn(`Retrying request... (${retries} retries left)`);
    await this.sleep(delay);

    try {
      const response = await requestFn();
      return {
        success: true,
        data: response.data,
        status: response.status,
        timestamp: new Date(),
      };
    } catch (retryError) {
      const apiError = this.handleError(retryError);
      return this.retryRequest(requestFn, retries - 1, delay * 2, apiError);
    }
  }

  /**
   * Generic request wrapper with error handling and retries
   */
  private async request<T>(
    requestFn: () => Promise<AxiosResponse<T>>,
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const retries = options.retries ?? this.config.retries;
    const retryDelay = options.retryDelay ?? this.config.retryDelay;

    try {
      const response = await requestFn();
      return {
        success: true,
        data: response.data,
        status: response.status,
        timestamp: new Date(),
      };
    } catch (error) {
      const apiError = this.handleError(error);
      this.logger.error('Request failed:', apiError.toJSON());

      // If skip error handling, return immediately
      if (options.skipErrorHandling) {
        return {
          success: false,
          error: apiError,
          status: apiError.status,
          timestamp: new Date(),
        };
      }

      // Attempt retries
      return this.retryRequest(requestFn, retries, retryDelay, apiError);
    }
  }

  /**
   * GET request
   */
  async get<T = any>(
    url: string,
    config?: AxiosRequestConfig,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    return this.request<T>(
      () => this.instance.get<T>(url, { ...config, signal: options?.signal }),
      options
    );
  }

  /**
   * POST request
   */
  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    return this.request<T>(
      () => this.instance.post<T>(url, data, { ...config, signal: options?.signal }),
      options
    );
  }

  /**
   * PUT request
   */
  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    return this.request<T>(
      () => this.instance.put<T>(url, data, { ...config, signal: options?.signal }),
      options
    );
  }

  /**
   * PATCH request
   */
  async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    return this.request<T>(
      () => this.instance.patch<T>(url, data, { ...config, signal: options?.signal }),
      options
    );
  }

  /**
   * DELETE request
   */
  async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    return this.request<T>(
      () => this.instance.delete<T>(url, { ...config, signal: options?.signal }),
      options
    );
  }

  /**
   * Get the underlying Axios instance
   */
  getInstance(): AxiosInstance {
    return this.instance;
  }
}

// Export singleton instance
export const httpClient = new HttpClient();

// Export for custom instances
export default httpClient;
